import standardLayoutRoute from '@input-web/modules/standard/views/layout/standard-layout.route';
import { ScoringStandardDetail } from '@standard/models/scoring-standard.model';
import {
  NavigateOptions,
  RegisteredRouter,
  useLocation,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import { isObjectTypeValid } from '@util/utils/object.util';
import pick from 'lodash/pick';
import { useCallback, useMemo } from 'react';
import { StandardListFilterSchema } from '../../models/standard-list-filter.model';

// #region Types
export type StandardLocationState = StandardListFilterSchema &
  Partial<
    Pick<
      ScoringStandardDetail,
      | 'bunriName'
      | 'partNumber'
      | 'partTitle'
      | 'partYear'
      | 'subjectName'
      | 'universityName'
      | 'facultyName'
    >
  >;
// #endregion

// #region Utils
function checkFilterQueryState(
  object?: object,
  type: 'every' | 'some' = 'some',
): StandardListFilterSchema | undefined {
  const keys = [
    'subjectId',
    'bunriId',
    'universityId',
    'facultyId',
  ] as (keyof StandardListFilterSchema)[];
  if (isObjectTypeValid<StandardListFilterSchema>(object, keys, type)) {
    return pick(object, keys);
  }

  return undefined;
}
// #endregion

export default function useStandardSearchState() {
  // #region Dependencies
  const search = useSearch({ from: standardLayoutRoute.id, shouldThrow: false });
  const location = useLocation();
  const navigate = useNavigate<RegisteredRouter, '/'>();
  // #endregion

  // #region Computed Value
  const state = useMemo<StandardLocationState | undefined>(() => {
    let standardState: StandardLocationState | undefined;

    const keys = [
      'bunriName',
      'partNumber',
      'partTitle',
      'partYear',
      'subjectName',
      'universityName',
      'facultyName',
    ] as (keyof StandardLocationState)[];
    if (isObjectTypeValid<StandardLocationState>(location.state, keys)) {
      standardState = pick(location.state, keys);
    }

    const checkedSearch = checkFilterQueryState(search);
    if (checkedSearch != null) return { ...standardState, ...checkedSearch };

    const checkedLocationState = checkFilterQueryState(location.state, 'every');
    if (checkedLocationState != null) return { ...standardState, ...checkedLocationState };

    return standardState;
  }, [search, location.state]);
  // #endregion

  // #region Handlers
  const handleNavigate = useCallback(
    (newSearch: StandardListFilterSchema, options?: NavigateOptions) =>
      navigate({ search: (current) => ({ ...current, ...newSearch }), state: true, ...options }),
    [navigate],
  );
  // #endregion

  return { handleNavigate, state };
}
